import { ArrowUpRight, Code2, FileJson, KeyRound } from 'lucide-react'
import { Link } from 'react-router-dom'
import { SiteLayout } from '@/components/site/SiteLayout'
import { CodeBlock, type CodeSample } from '@/components/docs/CodeBlock'

import { getApiBase, getApiHost } from '@/lib/api-base'

// Computed at module load using the current document host. Re-runs on a
// full page reload (every navigation in an SPA is the same load), so a
// user on geoborder.metaphase.tech sees metaphase-host examples and a
// user on geoborder.us sees the canonical host.
const API_BASE = getApiBase()
const API_HOST = getApiHost()

// ---------- code samples ----------------------------------------------------

// One generator drives every per-endpoint Request block so the curl + 5
// language samples stay consistent. `path` is the request path + query off
// API_BASE; auth defaults to true (Bearer key) - pass false for the public,
// keyless endpoints (sources / layers / health).
function reqSamples(path: string, opts: { auth?: boolean } = {}): CodeSample[] {
  const url = `${API_BASE}${path}`
  const auth = opts.auth !== false
  return [
    {
      label: 'curl',
      language: 'shell',
      source: auth
        ? `curl '${url}' \\\n  -H "Authorization: Bearer $GEOBORDER_API_KEY"`
        : `curl '${url}'`,
    },
    {
      label: 'JavaScript',
      language: 'javascript',
      source: auth
        ? `const res = await fetch('${url}', {\n  headers: { Authorization: \`Bearer \${apiKey}\` },\n})\nconst data = await res.json()`
        : `const res = await fetch('${url}')\nconst data = await res.json()`,
    },
    {
      label: 'Python',
      language: 'python',
      source: auth
        ? `import requests\n\nres = requests.get(\n    "${url}",\n    headers={"Authorization": f"Bearer {api_key}"},\n)\ndata = res.json()`
        : `import requests\n\ndata = requests.get("${url}").json()`,
    },
    {
      label: 'PHP',
      language: 'php',
      source: auth
        ? `<?php\n$ch = curl_init("${url}");\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\ncurl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer {$apiKey}"]);\n$data = json_decode(curl_exec($ch), true);\ncurl_close($ch);`
        : `<?php\n$ch = curl_init("${url}");\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\n$data = json_decode(curl_exec($ch), true);\ncurl_close($ch);`,
    },
    {
      label: 'C#',
      language: 'csharp',
      source: auth
        ? `using System.Net.Http;\nusing System.Net.Http.Headers;\n\nusing var http = new HttpClient();\nhttp.DefaultRequestHeaders.Authorization =\n    new AuthenticationHeaderValue("Bearer", apiKey);\nstring json = await http.GetStringAsync("${url}");`
        : `using System.Net.Http;\n\nusing var http = new HttpClient();\nstring json = await http.GetStringAsync("${url}");`,
    },
    {
      label: 'Java',
      language: 'java',
      source: auth
        ? `import java.net.URI;\nimport java.net.http.*;\n\nHttpClient client = HttpClient.newHttpClient();\nHttpRequest request = HttpRequest.newBuilder()\n    .uri(URI.create("${url}"))\n    .header("Authorization", "Bearer " + apiKey)\n    .build();\nHttpResponse<String> res =\n    client.send(request, HttpResponse.BodyHandlers.ofString());`
        : `import java.net.URI;\nimport java.net.http.*;\n\nHttpClient client = HttpClient.newHttpClient();\nHttpRequest request = HttpRequest.newBuilder()\n    .uri(URI.create("${url}"))\n    .build();\nHttpResponse<String> res =\n    client.send(request, HttpResponse.BodyHandlers.ofString());`,
    },
  ]
}

const LOCATION_SAMPLES = reqSamples('/location?lat=32.2226&lon=-110.9747')
const NEAREST_SAMPLES = reqSamples('/nearest?lat=32.1476&lon=-111.3075&include=place,county')
const BOUNDARY_SAMPLES = reqSamples('/boundaries/gb_county_04019')
const GEOMETRY_SAMPLES = reqSamples('/boundaries/gb_county_04019/geometry')
const USAGE_SAMPLES = reqSamples('/usage')
const SOURCES_SAMPLES = reqSamples('/sources', { auth: false })
const LAYERS_SAMPLES = reqSamples('/layers', { auth: false })
const HEALTH_SAMPLES = reqSamples('/health', { auth: false })

// Quickstart "make a call" uses the same language set as the endpoints.
const QUICKSTART_SAMPLES = reqSamples('/location?lat=32.2226&lon=-110.9747')

// ---------- endpoint descriptions ------------------------------------------

interface Endpoint {
  id: string
  method: 'GET'
  path: string
  title: string
  auth: 'Bearer key' | 'None'
  blurb: string
  samples: CodeSample[]
  response: string
}

const ENDPOINTS: Endpoint[] = [
  {
    id: 'location',
    method: 'GET',
    path: '/v1/location',
    title: 'Location',
    auth: 'Bearer key',
    blurb:
      'Return every jurisdiction whose boundary contains the supplied coordinate (polygon layers - country, state, county, court districts, etc.). Each hit carries a `nearest` block: distance (meters/miles/feet) + bearing (degrees + 16-point cardinal) to the nearest edge, plus `across` - the same-layer feature on the other side of that edge (or null at a coastline / international border). A bottom `nearest` section is always present: `country` (when the point is outside all land), `usa_border`, and `features[]` - the nearest point feature per point layer for U.S. coordinates. Scope `hits[]` and `features[]` with `include=`. To find the closest feature of a layer the point is NOT inside, use /v1/nearest. Every response also carries `in_united_states` (a boolean from the survey-grade definitive U.S. border - IBWC + IBC boundary data) and `distance_to_us_border` (signed: positive inside, negative outside), so you get an authoritative "is this in the U.S.?" answer accurate to the surveyed line. U.S. membership always comes from that surveyed line, so `in_united_states` and `hits[]` never disagree, and U.S. jurisdiction layers are never returned for a coordinate outside it. `strict_us_border=true` controls only foreign-country overlap: inside the U.S. it returns just the U.S. country hit, so a coarse Natural Earth polygon overhanging the border cannot also return Mexico or Canada.',
    samples: LOCATION_SAMPLES,
    response: `{
  "query": { "lat": 32.2226, "lon": -110.9747 },
  "hits": [
    {
      "geoborder_id": "gb_place_0477000",
      "name": "Tucson",
      "layer_id": "place",
      "identifiers": { "geoid": "0477000" },
      "source_id": "census_tiger_2024_place",
      "source": {
        "id": "census_tiger_2024_place",
        "name": "Census TIGER/Line 2024 Places",
        "org": "U.S. Census Bureau",
        "license": "U.S. government work - public domain"
      },
      "vintage": "2024",
      "effective_date": "2024-01-01",
      "nearest": {
        "distance_meters": 2157.0, "distance_miles": 1.34, "distance_feet": 7077.0,
        "bearing_degrees": 180.0, "cardinal": "S",
        "across": { "name": "South Tucson", "geoborder_id": "gb_place_0468850" }
      },
      "display_geom": null
    }
    // …more containing hits (country, state, county, congressional_district, …)
  ],
  "in_united_states": true,
  "distance_to_us_border": {
    "distance_meters": 95720.69, "distance_miles": 59.48, "distance_feet": 314044.26,
    "bearing_degrees": 168.4, "cardinal": "S"
  },
  "nearest": {
    "country": null,
    "usa_border": {
      "distance_meters": 95720.89, "distance_miles": 59.48, "distance_feet": 314044.0,
      "bearing_degrees": 195.44, "cardinal": "SSW",
      "adjacent_country": "Mexico", "label": "U.S. border with Mexico"
    },
    "features": []  // nearest point feature per point layer, for U.S. coordinates
  },
  "meta": { "api": "GeoBorder", "version": "v1", "endpoint": "/v1/location" }
}

// International waters / outside coverage: hits is empty, nearest.country
// is returned so the client still has actionable context.
{
  "query": { "lat": 0, "lon": -150 },
  "hits": [],
  "in_united_states": false,
  "distance_to_us_border": {
    "distance_meters": -4002100.0, "distance_miles": -2487.4, "distance_feet": -13130249.0,
    "bearing_degrees": 23.0, "cardinal": "NNE"
  },
  "nearest": {
    "country": {
      "geoborder_id": "gb_country_ki", "name": "Kiribati",
      "distance_meters": 708498.3, "distance_miles": 440.2,
      "bearing_degrees": 231.0, "cardinal": "SW"
    },
    "usa_border": {
      "distance_meters": 4002100.0, "distance_miles": 2487.4, "distance_feet": 13130249.0,
      "bearing_degrees": 23.0, "cardinal": "NNE",
      "adjacent_country": null, "label": "U.S. border"
    },
    "features": []
  },
  "meta": { "api": "GeoBorder", "version": "v1", "endpoint": "/v1/location" }
}`,
  },
  {
    id: 'nearest',
    method: 'GET',
    path: '/v1/nearest',
    title: 'Nearest',
    auth: 'Bearer key',
    blurb:
      'Return the single closest feature of each requested layer, regardless of containment - the complement to /v1/location. Use it to answer "what is the nearest place / county / ZIP code area" even when the point is inside none of them. `include=` is REQUIRED and names the layers to search; it also accepts the pseudo-ids `country` and `usa_border`, and the synth meta-id `federal_land`. One entry per requested id, in request order. `contains: true` with distance 0 when the point is inside a polygon layer; otherwise distance (meters/miles/feet) + bearing (degrees + cardinal) to the nearest feature. `format=geojson` is supported.',
    samples: NEAREST_SAMPLES,
    response: `{
  "query": { "lat": 32.1476, "lon": -111.3075 },
  "nearest": [
    {
      "geoborder_id": "gb_place_0473700",
      "name": "Three Points",
      "layer_id": "place",
      "contains": false,
      "distance_meters": 3896.0, "distance_miles": 2.42, "distance_feet": 12782.0,
      "bearing_degrees": 188.5, "cardinal": "S"
    },
    {
      "geoborder_id": "gb_county_04019",
      "name": "Pima County",
      "layer_id": "county",
      "contains": true,
      "distance_meters": 0, "distance_miles": 0, "distance_feet": 0,
      "bearing_degrees": null, "cardinal": null
    }
  ],
  "meta": {
    "api": "GeoBorder", "version": "v1",
    "endpoint": "/v1/nearest",
    "include": ["county", "place"]
  }
}`,
  },
  {
    id: 'boundary',
    method: 'GET',
    path: '/v1/boundaries/{id}',
    title: 'Boundary',
    auth: 'Bearer key',
    blurb:
      'Fetch metadata for one boundary by its stable GeoBorder id (e.g. gb_county_04019 for Pima County, Arizona). Returns identifiers, layer info, full source attribution, and links to the geometry endpoint.',
    samples: BOUNDARY_SAMPLES,
    response: `{
  "api": "GeoBorder",
  "version": "v1",
  "endpoint": "/v1/boundaries/gb_county_04019",
  "boundary": {
    "id": "gb_county_04019",
    "name": "Pima County",
    "layer": { "id": "county", "name": "County / County Equivalent", "category": "location" },
    "identifiers": { "geoid": "04019", "statefp": "04", "countyfp": "019" },
    "vintage": "2024",
    "effective_date": "2024-01-01",
    "properties": { "lsad": "06", "namelsad": "Pima County", "state_name": "Arizona", "state_abbrev": "AZ" },
    "source": {
      "id": "census_tiger_2024_county",
      "display_name": "Census TIGER 2024 - Counties",
      "source_org": "U.S. Census Bureau",
      "license": "U.S. government work - public domain",
      "vintage": "2024",
      "effective_date": "2024-01-01",
      "url": "https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html"
    },
    "links": {
      "self": "/v1/boundaries/gb_county_04019",
      "geometry": "/v1/boundaries/gb_county_04019/geometry"
    }
  },
  "generated_at": "2026-06-01T12:00:00.000Z"
}`,
  },
  {
    id: 'geometry',
    method: 'GET',
    path: '/v1/boundaries/{id}/geometry',
    title: 'Boundary geometry',
    auth: 'Bearer key',
    blurb:
      'Full GeoJSON geometry, centroid, and bounding box for one boundary. Always full precision - no simplification knob.',
    samples: GEOMETRY_SAMPLES,
    response: `{
  "api": "GeoBorder",
  "version": "v1",
  "endpoint": "/v1/boundaries/gb_county_04019/geometry",
  "boundary": {
    "id": "gb_county_04019",
    "name": "Pima County",
    "layer_id": "county",
    "vintage": "2024",
    "effective_date": "2024-01-01",
    "geometry": { "type": "MultiPolygon", "coordinates": [ /* … */ ] },
    "centroid": { "lon": -111.78, "lat": 32.04 },
    "bbox": [-113.34, 31.43, -110.45, 33.06]
  },
  "generated_at": "2026-06-01T12:00:00.000Z"
}`,
  },
  {
    id: 'usage',
    method: 'GET',
    path: '/v1/usage',
    title: 'Usage',
    auth: 'Bearer key',
    blurb:
      'Traffic counts (24h / 7d / 30d), latency percentiles, breakdowns by endpoint and status, and the 10 most recent requests scoped to the authenticating key.',
    samples: USAGE_SAMPLES,
    response: `{
  "api": "GeoBorder",
  "version": "v1",
  "endpoint": "/v1/usage",
  "key": { "id": "…", "name": "default" },
  "summary": {
    "calls_24h": 142, "calls_7d": 894, "calls_30d": 2160,
    "errors_24h": 0, "p50_ms": 48, "p95_ms": 91,
    "first_seen_at": "2026-05-01T14:22:08.000Z",
    "last_seen_at": "2026-05-27T11:58:42.000Z"
  },
  "by_endpoint": [{ "endpoint": "v1/location", "calls": 870 }],
  "by_status": [{ "status": 200, "calls": 894 }],
  "recent": [
    {
      "occurred_at": "2026-05-27T11:58:42.000Z",
      "endpoint": "v1/location",
      "method": "GET",
      "lat": 32.2226,
      "lon": -110.9747,
      "status": 200,
      "response_ms": 48.3,
      "layer_count": 5
    }
    // …up to 10 most recent
  ],
  "generated_at": "2026-05-27T12:00:00.000Z"
}`,
  },
  {
    id: 'layers',
    method: 'GET',
    path: '/v1/layers',
    title: 'Layers',
    auth: 'None',
    blurb:
      'Every logical layer GeoBorder can resolve (country, state, county, congressional district, …) with current boundaries row count per layer. Useful for checking what is actually live before integrating.',
    samples: LAYERS_SAMPLES,
    response: `{
  "api": "GeoBorder",
  "version": "v1",
  "endpoint": "/v1/layers",
  "layers": [
    {
      "id": "state",
      "name": "State / Territory",
      "description": "U.S. states + DC + territories from Census TIGER.",
      "category": "location",
      "default_in_response": true,
      "sort_order": 20,
      "boundaries_count": 56
    }
    // …one entry per layer GeoBorder currently serves
  ],
  "generated_at": "2026-05-27T12:00:00.000Z"
}`,
  },
  {
    id: 'sources',
    method: 'GET',
    path: '/v1/sources',
    title: 'Sources',
    auth: 'None',
    blurb:
      'Every dataset GeoBorder serves, with vintage, source organization, license, and current boundaries row count per source. No authentication required - provenance is a public good.',
    samples: SOURCES_SAMPLES,
    response: `{
  "api": "GeoBorder",
  "version": "v1",
  "endpoint": "/v1/sources",
  "sources": [
    {
      "id": "census_tiger_2025_state",
      "display_name": "Census TIGER 2025 - State",
      "source_org": "U.S. Census Bureau",
      "url": "https://www.census.gov/geographies/mapping-files/time-series/geo/tiger-line-file.html",
      "vintage": "2025",
      "effective_date": "2025-01-01",
      "license": "U.S. government work - public domain",
      "notes": null,
      "acquired_at": "2026-05-21T18:00:00.000Z",
      "boundaries_count": 56
    }
    // …one entry per dataset currently loaded
  ],
  "generated_at": "2026-05-27T12:00:00.000Z"
}`,
  },
  {
    id: 'health',
    method: 'GET',
    path: '/v1/health',
    title: 'Health check',
    auth: 'None',
    blurb:
      'Liveness probe plus the live boundaries row count. No authentication required.',
    samples: HEALTH_SAMPLES,
    response: `{
  "status": "ok",
  "version": "v1",
  "response_ms": 18.4,
  "boundaries_count": 4901,
  "error": null,
  "generated_at": "2026-05-22T18:23:11.244Z"
}`,
  },
]

const ERRORS: Array<{ code: string; status: string; meaning: string }> = [
  { code: 'MISSING_PARAMS', status: '400', meaning: 'A required query or path parameter was missing.' },
  { code: 'INVALID_PARAMS', status: '400', meaning: 'A parameter was present but invalid (e.g. lat outside -90..90).' },
  { code: 'UNAUTHENTICATED', status: '401', meaning: 'Missing or invalid Bearer key.' },
  { code: 'NOT_FOUND', status: '404', meaning: 'The requested resource (boundary id) does not exist.' },
  { code: 'METHOD_NOT_ALLOWED', status: '405', meaning: 'Endpoint only accepts GET.' },
  { code: 'GEOCODE_FAILED', status: '500', meaning: 'Internal error resolving the coordinate.' },
  { code: 'BOUNDARY_FETCH_FAILED', status: '500', meaning: 'Internal error retrieving boundary metadata.' },
  { code: 'GEOMETRY_FETCH_FAILED', status: '500', meaning: 'Internal error retrieving boundary geometry.' },
  { code: 'USAGE_FETCH_FAILED', status: '500', meaning: 'Internal error aggregating usage stats.' },
  { code: 'SOURCES_FETCH_FAILED', status: '500', meaning: 'Internal error reading the source catalog.' },
  { code: 'LAYERS_FETCH_FAILED', status: '500', meaning: 'Internal error reading the layer catalog.' },
  { code: 'RATE_LIMITED', status: '429', meaning: 'Per-key rate limit exceeded. Inspect Retry-After + the limits block in the response body.' },
]

// ---------- page -----------------------------------------------------------

export function ApiPage() {
  return (
    <SiteLayout>
      <div className="mx-auto max-w-4xl px-4 py-12 sm:py-16">
        {/* Hero */}
        <div className="mb-12">
          <span className="inline-block text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--color-accent)] font-[var(--font-mono)]">
            Developer API · v1
          </span>
          <h1 className="mt-2 text-[34px] font-semibold tracking-tight text-[var(--color-text-strong)] leading-[1.1]">
            GeoBorder API
          </h1>
          <p className="mt-3 text-[15.5px] leading-relaxed text-[var(--color-muted)]">
            Reverse-geocode a coordinate to its full stack of U.S. jurisdictions -
            country, state, county, CBSA, congressional district, and more - with
            stable IDs, source provenance, and edge context. Authenticate with a
            bearer key and you're live.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-elev)] px-3 py-1 text-[12px] text-[var(--color-muted)] font-[var(--font-mono)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-signal)]" aria-hidden />
              {API_HOST}/v1
            </span>
            <Link
              to="/docs"
              className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-elev)] px-3 py-1 text-[12px] text-[var(--color-muted)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/60 transition-colors"
            >
              <FileJson size={12} aria-hidden />
              OpenAPI reference
            </Link>
            <a
              href="/openapi.json"
              className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-elev)] px-3 py-1 text-[12px] text-[var(--color-muted)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/60 transition-colors font-[var(--font-mono)]"
            >
              openapi.json
              <ArrowUpRight size={12} aria-hidden />
            </a>
          </div>
        </div>

        {/* Quickstart */}
        <section id="quickstart" className="mb-14 scroll-mt-24">
          <h2 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-strong)]">
            Quickstart
          </h2>
          <p className="mt-2 text-[14px] leading-relaxed text-[var(--color-muted)]">
            Three steps to your first response.
          </p>

          <ol className="mt-6 space-y-6">
            <li className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-baseline">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)] font-[var(--font-mono)] text-[12px]">
                1
              </span>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
                  Create an account
                </h3>
                <p className="mt-1 text-[14px] leading-relaxed text-[var(--color-muted)]">
                  <Link to="/sign-up" className="text-[var(--color-accent)] underline underline-offset-2">
                    Sign up
                  </Link>{' '}
                  with email + password, verify, and you're in.
                </p>
              </div>
            </li>

            <li className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-baseline">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)] font-[var(--font-mono)] text-[12px]">
                2
              </span>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
                  Generate an API key
                </h3>
                <p className="mt-1 text-[14px] leading-relaxed text-[var(--color-muted)]">
                  Go to{' '}
                  <Link to="/account/keys" className="text-[var(--color-accent)] underline underline-offset-2">
                    Account → API keys
                  </Link>{' '}
                  and click <em>Generate key</em>. Copy the plaintext immediately - it's only
                  shown once. Format: <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">gb_live_&lt;64 hex chars&gt;</code>.
                </p>
              </div>
            </li>

            <li className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-baseline">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)] font-[var(--font-mono)] text-[12px]">
                3
              </span>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
                  Make a call
                </h3>
                <p className="mt-1 text-[14px] leading-relaxed text-[var(--color-muted)]">
                  Send the key as an <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">Authorization: Bearer …</code> header.
                </p>
                <div className="mt-4">
                  <CodeBlock samples={QUICKSTART_SAMPLES} />
                </div>
              </div>
            </li>
          </ol>
        </section>

        {/* Auth callout */}
        <section className="mb-14 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-5">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] bg-[var(--color-accent-soft)] text-[var(--color-accent)] shrink-0"
            >
              <KeyRound size={18} strokeWidth={2.2} />
            </span>
            <div>
              <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
                Authentication
              </h3>
              <p className="mt-1 text-[14px] leading-relaxed text-[var(--color-muted)]">
                Every authenticated endpoint requires an <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">Authorization: Bearer gb_live_…</code> header.
                Keys are scoped to the organization they were generated in and can be revoked instantly from{' '}
                <Link to="/account/keys" className="text-[var(--color-accent)] underline underline-offset-2">
                  Account → API keys
                </Link>
                . The plaintext key is shown exactly once at creation - store it in a secret
                manager, never commit it.
              </p>
            </div>
          </div>
        </section>

        {/* Rate limits */}
        <section id="rate-limits" className="mb-14 scroll-mt-24">
          <h2 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-strong)]">
            Rate limits
          </h2>
          <p className="mt-2 text-[14px] leading-relaxed text-[var(--color-muted)]">
            Every authenticated endpoint is rate-limited per key. The default tier allows{' '}
            <strong className="text-[var(--color-text-strong)]">120 requests / minute</strong> and{' '}
            <strong className="text-[var(--color-text-strong)]">50,000 requests / UTC day</strong>.
            Need more? Contact support to request a higher limit.
          </p>
          <div className="mt-5 overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)]">
            <table className="w-full text-left">
              <thead className="border-b border-[var(--color-border)]">
                <tr>
                  <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Header
                  </th>
                  <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Meaning
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['X-RateLimit-Limit', 'Per-minute cap for the calling key.'],
                  ['X-RateLimit-Remaining', 'Requests left in the current minute window.'],
                  ['X-RateLimit-Reset', 'Unix epoch (seconds) when the minute window resets.'],
                  ['X-RateLimit-Tier', 'Calling key’s assigned tier (e.g. default, pro).'],
                  ['Retry-After', 'Seconds to wait before the next attempt. Only on 429 responses.'],
                ].map(([h, d], i) => (
                  <tr
                    key={h}
                    className={i === 0 ? '' : 'border-t border-[var(--color-border-soft)]'}
                  >
                    <td className="px-4 py-3 font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">
                      {h}
                    </td>
                    <td className="px-4 py-3 text-[13.5px] text-[var(--color-muted)] leading-relaxed">
                      {d}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-[13.5px] text-[var(--color-muted)]">
            Over-limit responses return <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">HTTP 429</code> with a{' '}
            <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">RATE_LIMITED</code> code and the full per-minute and per-day
            counters in the body so clients can pace themselves intelligently:
          </p>
          <div className="mt-3">
            <CodeBlock
              samples={[
                {
                  label: 'json',
                  language: 'json',
                  source: `{
  "error": "Rate limit exceeded for this key.",
  "code": "RATE_LIMITED",
  "retry_after": 47,
  "limits": {
    "per_minute": 120,
    "per_day": 50000,
    "remaining_minute": 0,
    "remaining_day": 47821,
    "reset_minute": 1779563400,
    "reset_day": 1779580800,
    "tier": "default"
  }
}`,
                },
              ]}
            />
          </div>
        </section>

        {/* Endpoints */}
        <section id="endpoints" className="mb-14 scroll-mt-24">
          <h2 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-strong)]">
            Endpoints
          </h2>
          <p className="mt-2 text-[14px] leading-relaxed text-[var(--color-muted)]">
            All paths are relative to <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">{API_BASE}</code>.
          </p>

          <div className="mt-6 space-y-10">
            {ENDPOINTS.map((e) => (
              <article id={e.id} key={e.id} className="scroll-mt-24">
                <header>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-[var(--color-accent-soft)] px-2 py-0.5 font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--color-accent)]">
                      {e.method}
                    </span>
                    <code className="font-[var(--font-mono)] text-[14px] text-[var(--color-text-strong)]">
                      {e.path}
                    </code>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-elev)] px-2 py-0.5 font-[var(--font-mono)] text-[10.5px] uppercase tracking-[0.1em] text-[var(--color-muted)]">
                      Auth · {e.auth}
                    </span>
                  </div>
                  <h3 className="mt-2 text-[17px] font-semibold tracking-tight text-[var(--color-text-strong)]">
                    {e.title}
                  </h3>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-[var(--color-muted)]">
                    {e.blurb}
                  </p>
                </header>

                <div className="mt-4">
                  <CodeBlock title="Request" samples={e.samples} />
                </div>
                <div className="mt-3">
                  <CodeBlock
                    title="Response"
                    samples={[{ label: 'json', language: 'json', source: e.response }]}
                  />
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Errors */}
        <section id="errors" className="mb-14 scroll-mt-24">
          <h2 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-strong)]">
            Errors
          </h2>
          <p className="mt-2 text-[14px] leading-relaxed text-[var(--color-muted)]">
            Every error response is JSON with <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">error</code> and{' '}
            <code className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)]">code</code> fields. The code is stable;
            the error string is for humans and may change.
          </p>
          <div className="mt-5 overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)]">
            <table className="w-full text-left">
              <thead className="border-b border-[var(--color-border)]">
                <tr>
                  <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Code
                  </th>
                  <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Status
                  </th>
                  <th className="px-4 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Meaning
                  </th>
                </tr>
              </thead>
              <tbody>
                {ERRORS.map((row, i) => (
                  <tr
                    key={row.code}
                    className={i === 0 ? '' : 'border-t border-[var(--color-border-soft)]'}
                  >
                    <td className="px-4 py-3 font-[var(--font-mono)] text-[12.5px] text-[var(--color-text-strong)] whitespace-nowrap">
                      {row.code}
                    </td>
                    <td className="px-4 py-3 font-[var(--font-mono)] text-[12.5px] text-[var(--color-muted)]">
                      {row.status}
                    </td>
                    <td className="px-4 py-3 text-[13.5px] text-[var(--color-muted)] leading-relaxed">
                      {row.meaning}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pointers */}
        <section className="grid gap-4 sm:grid-cols-2">
          <Link
            to="/docs"
            className="block rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-5 transition-colors hover:border-[var(--color-accent)]/60"
          >
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-[14px] font-semibold text-[var(--color-text-strong)]">
                OpenAPI reference
              </h3>
              <Code2 size={14} className="text-[var(--color-muted)]" aria-hidden />
            </div>
            <p className="mt-1.5 text-[13px] text-[var(--color-muted)]">
              Browse every endpoint, schema, and parameter in an interactive viewer.
            </p>
          </Link>

          <Link
            to="/account/keys"
            className="block rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-5 transition-colors hover:border-[var(--color-accent)]/60"
          >
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-[14px] font-semibold text-[var(--color-text-strong)]">
                Manage API keys
              </h3>
              <KeyRound size={14} className="text-[var(--color-muted)]" aria-hidden />
            </div>
            <p className="mt-1.5 text-[13px] text-[var(--color-muted)]">
              Generate a new key, view usage, or revoke an existing one.
            </p>
          </Link>
        </section>
      </div>
    </SiteLayout>
  )
}
